import { z } from 'zod';

/**
 * FMV Outlook If Held DTOs (Asset Detail Visualizations design doc).
 *
 * Backs:
 *   GET /api/client/assets/:assetId/fmv-outlook   → assetFmvOutlookResponseSchema
 *   GET /api/assets/:assetId/fmv-outlook          → assetFmvOutlookResponseSchema
 *   GET /api/client/dashboard/fmv-outlook         → dashboardFmvOutlookResponseSchema
 *
 * Client cone invariant (clientDashboard.ts): the client endpoints' lo/hi are
 * the FIXED symmetric CLIENT_OUTLOOK_CONE_PCT — never the ops-internal stored
 * fmv low/high bands. Only the ops endpoint derives the cone from real bands.
 */

export const fmvOutlookPointSchema = z.object({
  /** 'YYYY-MM'. Point 0 = current month / current FMV median. */
  month: z.string(),
  medUsd: z.coerce.number(),
  loUsd: z.coerce.number(),
  hiUsd: z.coerce.number(),
});
export type FmvOutlookPointDto = z.infer<typeof fmvOutlookPointSchema>;

export const bookLinePointSchema = z.object({
  month: z.string(),
  valueUsd: z.coerce.number(),
});
export type BookLinePointDto = z.infer<typeof bookLinePointSchema>;

export const refreshCliffSchema = z.object({
  /** The date the depreciation curve falls to 80% of its anchor value. May be in the past. */
  month: z.string(),
});
export type RefreshCliffDto = z.infer<typeof refreshCliffSchema>;

/** Where the book line's price anchor came from. */
export const bookAnchorSourceSchema = z.enum(['purchase_price', 'launch_price']);

export const assetFmvOutlookResponseSchema = z.object({
  /**
   * Month 0 → horizon, monthly. Empty when the asset has no snapshot or its
   * class has no active age-curve bracket for its age — the UI renders the
   * history line only, never a $0 forecast.
   */
  forecast: z.array(fmvOutlookPointSchema),
  /** Median delta if held to the horizon. Null when no forecast. */
  heldDeltaUsd: z.coerce.number().nullable(),
  refreshCliff: refreshCliffSchema.nullable(),
  /** Anchor date → horizon, monthly. Null when anchor/rate is missing. */
  bookLine: z.array(bookLinePointSchema).nullable(),
  bookAnchorSource: bookAnchorSourceSchema.nullable(),
  horizonMonths: z.number().int(),
  /** Axis origin, 'YYYY-MM': coalesce(sku launch date, asset addition date). */
  windowStartMonth: z.string().nullable(),
  /**
   * SIL-1828 (R2): true when the asset's latest snapshot is priced from a
   * virtual (sum-of-components) baseline. The forecast is shown but must carry
   * the "Directional" label. Server-derived; UIs never re-derive.
   */
  isDirectionalBaseline: z.boolean().default(false),
  /** SIL-2504: 'sku_match_unverified' when the asset's SKU match is inferred - forecast is empty because FMV is withheld, not because there is no data. */
  fmvWithheldReason: z.literal('sku_match_unverified').nullable().optional(),
});
export type AssetFmvOutlookResponse = z.infer<typeof assetFmvOutlookResponseSchema>;

/** One asset class's aggregated outlook on the dashboard. */
export const dashboardFmvOutlookClassSchema = z.object({
  classId: z.string(),
  className: z.string(),
  assetCount: z.number().int(),
  /**
   * Summed per-class forecast of medians. Median-only at the aggregate level:
   * summing per-asset intervals is not a portfolio interval, so no cone here.
   */
  forecast: z.array(
    z.object({ month: z.string(), medUsd: z.coerce.number() }),
  ),
  heldDeltaUsd: z.coerce.number().nullable(),
  /** SIL-1828: true when any asset summed into this class is priced from a virtual baseline. */
  includesDirectionalBaseline: z.boolean().default(false),
});
export type DashboardFmvOutlookClass = z.infer<typeof dashboardFmvOutlookClassSchema>;

export const dashboardFmvOutlookResponseSchema = z.object({
  classes: z.array(dashboardFmvOutlookClassSchema),
  /** Portfolio-wide median delta if everything is held to the horizon. */
  totalHeldDeltaUsd: z.coerce.number().nullable(),
  horizonMonths: z.number().int(),
  /** SIL-1828: any class above includes a virtually-priced asset. */
  includesDirectionalBaseline: z.boolean().default(false),
});
export type DashboardFmvOutlookResponse = z.infer<typeof dashboardFmvOutlookResponseSchema>;
